import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

import { extractErrorMessage } from '../../api/errors';
import { SearchableSelect } from '../../components/SearchableSelect';
import { SortHeader } from '../../components/SortHeader';
import { useSortedRows } from '../../util/tableSort';
import {
  approveCohortSubmission,
  listAllCohortSubmissions,
  rejectCohortSubmission,
  type CohortSubmission,
  type MetricStat,
} from '../../api/cohortBaseline';

/**
 * Superadmin review queue for tenant-minted master baselines
 * (master-baseline-cohort-submit PR3).
 *
 * Tenants submit cohort baselines (filter → preview → tag → submit) from
 * the tenant Cohort baseline page. Here a superadmin reviews each one —
 * expand to see the frozen per-metric stats — and either Approves (which
 * promotes median + mean into public.master_baselines, tagged
 * source='tenant-submitted:<slug>', so the faculty bars light up) or
 * Rejects it. Already-curated tuples (e.g. an IRCAD seed) are never
 * overwritten; the approve result reports created vs skipped.
 */

function fmt(v: number | null): string {
  if (v === null || v === undefined) return '—';
  if (Number.isInteger(v)) return String(v);
  return Number(v.toFixed(3)).toString();
}

const NO_SUBMISSIONS: CohortSubmission[] = [];

// `submitted` has no header of its own: it is the default order (newest
// first, as the API returns it) until a column header is clicked.
const SUBMISSION_SORT = {
  submitted: (s: CohortSubmission) => s.created_at,
  tenant: (s: CohortSubmission) => s.tenant_name ?? s.tenant_slug ?? null,
  procedure: (s: CohortSubmission) => s.procedure_name ?? 'All',
  cohort: (s: CohortSubmission) => s.cohort,
  sessions: (s: CohortSubmission) => s.session_count,
  metrics: (s: CohortSubmission) => s.metrics.length,
  status: (s: CohortSubmission) => s.status,
};

const METRIC_SORT = {
  metric: (m: MetricStat) => m.metric,
  median: (m: MetricStat) => m.median,
  mean: (m: MetricStat) => m.mean,
  p25: (m: MetricStat) => m.p25,
  p75: (m: MetricStat) => m.p75,
  n: (m: MetricStat) => m.n,
};

// A lifecycle sequence, so kept in order (not alphabetized).
const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];
const STATUS_PINNED = [{ value: '', label: 'All' }];

function statusColor(
  status: string,
): 'default' | 'success' | 'error' | 'warning' {
  if (status === 'approved') return 'success';
  if (status === 'rejected') return 'error';
  return 'warning';
}

export function MasterBaselineReview({
  embedded = false,
  actionsSlot = null,
}: { embedded?: boolean; actionsSlot?: HTMLElement | null } = {}) {
  const [statusFilter, setStatusFilter] = useState('pending');
  const [submissions, setSubmissions] = useState<CohortSubmission[] | null>(
    null,
  );
  const [loadError, setLoadError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      setSubmissions(await listAllCohortSubmissions(statusFilter || null));
    } catch (err) {
      setLoadError(extractErrorMessage(err, 'failed to load submissions'));
    }
  }, [statusFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  const { sort, sorted } = useSortedRows(
    submissions ?? NO_SUBMISSIONS,
    SUBMISSION_SORT,
    'submitted',
    'desc',
  );

  const statusFilterControl = (
    <SearchableSelect
      id="mbr-status"
      label="Status"
      value={statusFilter}
      onChange={setStatusFilter}
      options={STATUS_OPTIONS}
      pinned={STATUS_PINNED}
      keepOrder
      sx={{ minWidth: 180 }}
    />
  );

  return (
    <Stack spacing={3} sx={{ maxWidth: 1100 }}>
      {!embedded && (
        <Stack spacing={0.5}>
          <Typography variant="h4">Master-baseline submissions</Typography>
          <Typography variant="body2" color="text.secondary">
            Tenants without IRCAD-style master data mint their own from their
            captured sessions. Approving a submission promotes its median +
            mean into the shared master_baselines catalogue (tagged{' '}
            <code>tenant-submitted:&lt;slug&gt;</code>) so the faculty
            comparison bars render for that cohort. Already-curated values
            are never overwritten.
          </Typography>
        </Stack>
      )}

      {notice && (
        <Alert severity="success" onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}
      {loadError && <Alert severity="error">{loadError}</Alert>}

      {actionsSlot ? (
        createPortal(statusFilterControl, actionsSlot)
      ) : (
        <Stack direction="row" alignItems="center" spacing={2}>
          {statusFilterControl}
        </Stack>
      )}

      <Paper>
        {!submissions && !loadError && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={20} />
          </Box>
        )}
        {submissions && submissions.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            No {statusFilter || ''} submissions.
          </Typography>
        )}
        {submissions && submissions.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={40} />
                  <SortHeader label="Tenant" col="tenant" sort={sort} />
                  <SortHeader label="Procedure" col="procedure" sort={sort} />
                  <SortHeader label="Cohort" col="cohort" sort={sort} />
                  <SortHeader label="Sessions" col="sessions" sort={sort} align="right" />
                  <SortHeader label="Metrics" col="metrics" sort={sort} align="right" />
                  <SortHeader label="Status" col="status" sort={sort} />
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sorted.map((s) => (
                  <SubmissionRow
                    key={s.id}
                    submission={s}
                    onChanged={load}
                    onNotice={setNotice}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Stack>
  );
}

function SubmissionRow({
  submission,
  onChanged,
  onNotice,
}: {
  submission: CohortSubmission;
  onChanged: () => void;
  onNotice: (msg: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pending = submission.status === 'pending';
  const { sort: metricSort, sorted: metrics } = useSortedRows(
    submission.metrics,
    METRIC_SORT,
    'metric',
  );

  const handleApprove = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await approveCohortSubmission(submission.id);
      onNotice(
        `Approved — ${res.baselines_created} baseline row(s) created` +
          (res.baselines_skipped
            ? `, ${res.baselines_skipped} skipped (already curated)`
            : ''),
      );
      onChanged();
    } catch (err) {
      setError(extractErrorMessage(err, 'approve failed'));
    } finally {
      setBusy(false);
    }
  };

  const handleReject = async () => {
    setBusy(true);
    setError(null);
    try {
      const note = window.prompt('Reason for rejection (optional):') ?? null;
      await rejectCohortSubmission(submission.id, note);
      onNotice('Submission rejected.');
      onChanged();
    } catch (err) {
      setError(extractErrorMessage(err, 'reject failed'));
    } finally {
      setBusy(false);
    }
  };

  const f = submission.filter;
  const filterBits = [
    f.title ? `title: ${f.title}` : null,
    f.min_years !== null ? `≥${f.min_years}y` : null,
    f.max_years !== null ? `≤${f.max_years}y` : null,
  ].filter(Boolean);

  return (
    <>
      <TableRow hover>
        <TableCell>
          <IconButton
            size="small"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? 'collapse' : 'expand'}
          >
            {open ? (
              <KeyboardArrowUpIcon fontSize="small" />
            ) : (
              <KeyboardArrowDownIcon fontSize="small" />
            )}
          </IconButton>
        </TableCell>
        <TableCell>{submission.tenant_name ?? submission.tenant_slug ?? '—'}</TableCell>
        <TableCell>{submission.procedure_name ?? 'All'}</TableCell>
        <TableCell>{submission.cohort}</TableCell>
        <TableCell align="right">{submission.session_count}</TableCell>
        <TableCell align="right">
          {submission.metrics.length}
          {pending && submission.would_skip != null && (
            <Typography variant="caption" color="text.secondary" display="block">
              {submission.would_create} new
              {submission.would_skip > 0
                ? ` · ${submission.would_skip} skip`
                : ''}
            </Typography>
          )}
        </TableCell>
        <TableCell>
          <Chip
            label={submission.status}
            color={statusColor(submission.status)}
            size="small"
          />
        </TableCell>
        <TableCell align="right">
          {pending ? (
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button
                size="small"
                variant="contained"
                disabled={busy}
                onClick={() => void handleApprove()}
              >
                Approve
              </Button>
              <Button
                size="small"
                color="error"
                disabled={busy}
                onClick={() => void handleReject()}
              >
                Reject
              </Button>
            </Stack>
          ) : (
            <Typography variant="caption" color="text.secondary">
              reviewed
            </Typography>
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ py: 0, border: 0 }} colSpan={8}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ py: 2, px: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Frozen stats {filterBits.length > 0 && `(${filterBits.join(', ')})`}
              </Typography>
              {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <SortHeader label="Metric" col="metric" sort={metricSort} />
                    <SortHeader label="Median" col="median" sort={metricSort} align="right" />
                    <SortHeader label="Mean" col="mean" sort={metricSort} align="right" />
                    <SortHeader label="p25" col="p25" sort={metricSort} align="right" />
                    <SortHeader label="p75" col="p75" sort={metricSort} align="right" />
                    <SortHeader label="N" col="n" sort={metricSort} align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {metrics.map((m) => (
                    <TableRow key={m.metric}>
                      <TableCell>
                        {m.metric}
                        {m.unit ? ` (${m.unit})` : ''}
                      </TableCell>
                      <TableCell align="right">{fmt(m.median)}</TableCell>
                      <TableCell align="right">{fmt(m.mean)}</TableCell>
                      <TableCell align="right">{fmt(m.p25)}</TableCell>
                      <TableCell align="right">{fmt(m.p75)}</TableCell>
                      <TableCell align="right">{m.n}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}
